import {
  faArrowRotateLeft,
  faPlus,
  faQuestionCircle,
  faTrashCan,
} from '@fortawesome/pro-regular-svg-icons'
import {
  ActionIcon,
  Box,
  Button,
  Card,
  Flex,
  NumberInput,
  Skeleton,
  Text,
  Tooltip,
} from '@mantine/core'
import { useEffect } from 'react'

import { useAnalytics } from '@flecto/analytics'
import { useTranslation } from '@flecto/translations'
import {
  type CoreItem,
  type CoreEstimateInsuranceForQuote,
} from '@flecto/types'
import {
  AvatarItem,
  DataTable,
  type DataTableColumn,
  Icon,
  TextCurrency,
} from '@flecto/ui'

import { useModalProductSelect } from '../../../insurances/hooks/useModalProductSelect'
import { useFormContext } from '../FormCreateInsurancePolicy/FormCreateInsurancePolicy'
import { EditableCurrencyValue } from './EditableCurrencyValue'

type ProductState = {
  localPrice: number
  localQuantity: number
  isPriceManuallyChanged: boolean
  rentalValue: number
  isLoadingEstimate: boolean
  isLoadingEstimateQuote: boolean
  isRefetchingEstimate: boolean
}

type ProductActions = {
  setLocalPrice: (value: number) => void
  setLocalQuantity: (value: number) => void
  deleteProduct: () => void
  handleItemSelection?: (field: string, value: CoreItem) => void
  setResetRentalValue: (value: boolean) => void
  setProductInitialValues: (value: Partial<CoreItem>) => void
}

type CardProductSelectProps = {
  estimateQuote: CoreEstimateInsuranceForQuote
  productState: ProductState
  productActions: ProductActions
}

export function CardProductSelect({
  estimateQuote,
  productState,
  productActions,
}: CardProductSelectProps) {
  const track = useAnalytics('segment')
  const form = useFormContext()
  const { t } = useTranslation('saas', { keyPrefix: 'insurances' })

  const { openModal } = useModalProductSelect({
    setItem: productActions.handleItemSelection || form.setFieldValue,
    setProductInitialValues: productActions.setProductInitialValues,
  })

  const isLoading =
    productState.isLoadingEstimateQuote ||
    productState.isLoadingEstimate ||
    productState.isRefetchingEstimate ||
    (productState.localPrice === 0 && productState.isPriceManuallyChanged)

  const columns: DataTableColumn<Partial<CoreItem>>[] = [
    {
      accessor: 'product',
      title: t('insurances_details.label_product_title'),
      width: 250,
      render: () => (
        <Flex align='center' gap='sm'>
          <AvatarItem size='sm' item={form.values?.item as CoreItem} />
          <Flex direction='column'>
            <Text fw={500}>{form.values.item?.title ?? '-'}</Text>
            <Text size='sm'>
              {form.values.item?.allow_overbooking
                ? t('create_insurance.label_unlimited_stock')
                : t('create_insurance.label_limited_stock', {
                    quantity: form.values.item?.quantity,
                  })}
            </Text>
          </Flex>
        </Flex>
      ),
    },
    {
      accessor: 'quantity',
      title: t('insurances_details.label_product_quantity'),
      render: () => (
        <NumberInput
          disabled={
            (!form.values.item?.allow_overbooking &&
              form.values.item?.quantity === 1) ||
            productState.isLoadingEstimateQuote ||
            productState.isLoadingEstimate ||
            productState.isRefetchingEstimate
          }
          w={110}
          min={1}
          step={1}
          max={
            form.values.item?.allow_overbooking
              ? Infinity
              : form.values.item?.quantity
          }
          value={Number(productState.localQuantity)}
          onChange={(value) => {
            productActions.setLocalQuantity(Number(value))
          }}
          size='sm'
        />
      ),
    },
    {
      accessor: 'price_per_unit',
      title: (
        <Flex align='center' gap='xs'>
          <Text>{t('insurances_details.label_product_price_per_unit')}</Text>
          <Tooltip
            label={t(
              'create_insurance.insurance_details.price_per_unit_tooltip'
            )}
          >
            <Box mt={4}>
              <Icon icon={faQuestionCircle} color='dimmed' size='xs' />
            </Box>
          </Tooltip>
        </Flex>
      ),
      render: () => (
        <Skeleton h={20} width={100} visible={isLoading}>
          <EditableCurrencyValue
            value={
              productState.isPriceManuallyChanged
                ? productState.localPrice
                : productState.rentalValue
            }
            disabled={
              productState.isLoadingEstimateQuote ||
              productState.isLoadingEstimate ||
              productState.isRefetchingEstimate
            }
            onChange={(value) => productActions.setLocalPrice(value)}
          />
        </Skeleton>
      ),
    },
    {
      accessor: 'rental_value',
      title: (
        <Flex align='center' gap='xs'>
          <Text>{t('insurances_details.label_product_rental_value')}</Text>
          <Tooltip
            label={t('create_insurance.insurance_details.rental_value_tooltip')}
          >
            <Box mt={4}>
              <Icon icon={faQuestionCircle} color='dimmed' size='xs' />
            </Box>
          </Tooltip>
        </Flex>
      ),
      render: () => (
        <Skeleton height={20} width={100} visible={isLoading}>
          <TextCurrency
            cents={
              productState.isPriceManuallyChanged
                ? productState.localPrice * (form.values.quantity ?? 0)
                : productState.rentalValue * (form.values.quantity ?? 0)
            }
          />
        </Skeleton>
      ),
    },
    {
      accessor: 'est_coverage_cost',
      title: (
        <Flex align='center' gap='xs'>
          <Text>
            {t('create_insurance.insurance_details.est_coverage_cost')}
          </Text>
          <Tooltip
            label={t(
              'create_insurance.insurance_details.est_coverage_cost_tooltip'
            )}
          >
            <Box mt={4}>
              <Icon icon={faQuestionCircle} color='dimmed' size='xs' />
            </Box>
          </Tooltip>
        </Flex>
      ),
      render: () => (
        <Skeleton height={20} width={100} visible={isLoading}>
          <TextCurrency cents={estimateQuote?.price_cents ?? 0} />
        </Skeleton>
      ),
    },
    {
      accessor: 'actions',
      title: '',
      render: () => (
        <Flex justify='flex-end'>
          <ActionIcon size='xs' onClick={productActions.deleteProduct}>
            <Icon icon={faTrashCan} />
          </ActionIcon>
        </Flex>
      ),
    },
  ]

  useEffect(() => {
    if (form.values?.item) {
      form.setFieldValue('item_id', form.values.item.id)
      form.setFieldValue('quantity', Number(productState.localQuantity))
      form.setFieldValue(
        'incidence_value_cents',
        productState.isPriceManuallyChanged
          ? productState.localPrice
          : productState.rentalValue
      )
    }
  }, [form.values?.item, productState.rentalValue])

  return (
    <Card withBorder>
      <Flex align='center' justify='space-between' mb='lg'>
        <Box style={{ flex: 1 }}>
          <Text fw={500}>{t('create_insurance.select_product.title')}</Text>
          <Text c='dimmed' size='sm'>
            {t('create_insurance.select_product.description')}
          </Text>
        </Box>
      </Flex>
      {Boolean(form.values?.item) && (
        <Flex direction='column'>
          <DataTable
            withBorder={false}
            columns={columns}
            records={form.values.item ? [form.values.item] : []}
            horizontalSpacing='md'
            pinLastColumn
          />
          <Button
            style={{ alignSelf: 'flex-end' }}
            px={0}
            variant='transparent'
            color='blue'
            onClick={() => {
              productActions.setResetRentalValue(true)
              productActions.setLocalPrice(0)
            }}
          >
            <Icon icon={faArrowRotateLeft} mr='sm' />
            {t('create_insurance.select_product.reset_rental_values_btn')}
          </Button>
        </Flex>
      )}
      {!form.values.item && (
        <Button
          data-testid='select-product-btn'
          variant='outline'
          leftSection={<Icon icon={faPlus} />}
          onClick={() => {
            openModal()
            track('INSURANCE_FORM__ITEM_NEW', {})
          }}
          disabled={!form.values.start_at || !form.values.end_at}
        >
          {t('create_insurance.select_product.select_product_btn')}
        </Button>
      )}
    </Card>
  )
}
